import { ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable, Pagination, CreateEntityModal, ListToolbar, SchemaField } from '../organisms';
import { useServerList } from '../lib/useServerList';
import { formatDate, isDateFieldName, isIsoDateString, isYearFieldName } from '../lib/orgTimezone';
import { tableToEntity } from '../lib/tableEntity';
import { humanizeToken } from '../lib/inlineAttribution';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * GenericList — schema-driven list page for any entity table.
 *
 * Renders /api/<table> rows in a DataTable using columns derived from
 * /api/schema/<table>: visible non-hidden non-pk fields, sorted by
 * displayOrder, capped at 6 columns to keep dense rows scannable.
 * "+ New" opens CreateEntityModal. Row click navigates to
 * /<routePath>/:id.
 *
 * Server-side list (PRD 06.1): the rows come from the generic-CRUD list
 * envelope (`GET /api/<resource>?q&sort&dir&page&limit`), so the sortable
 * headers + pager are server-side. A search box is shown ONLY when the
 * table is searchable — i.e. the schema endpoint reports a non-empty
 * `searchColumns` (the SEARCH_COLUMNS the `?q=` LIKE-match uses). Tables the
 * backend genuinely can't free-text search (no SEARCH_COLUMNS entry) get no
 * search box rather than one that silently does nothing.
 *
 * Wired by route config in main.tsx (Block 9 PR 1). Use this for the
 * ~35 "supporting" entities (assessors, collectors, jurisdictions,
 * contacts, etc.) where the canonical Client/Property/Parcel
 * patterns are overkill.
 *
 * Phase 11 Block 9 PR 1 (2026-05-12); search bar restored (lost-search-bars fix).
 */

export interface GenericListConfig {
  table: string;                                      // snake_case, e.g., 'assessors'
  routePath?: string;                                 // URL segment (default: table with _ → -)
  apiPath?: string;                                   // API segment (default: table with _ → -)
  title: string;                                      // page heading + button label root
  columns?: string[];                                 // override auto-derived columns (field names)
  excludeColumns?: string[];                          // drop these field names from the auto-derived list
  filterField?: {                                     // optional single-column Select filter in the toolbar
    column: string;                                   //   (server-side equality via crud SCALAR_FILTERS)
    placeholder: string;
    options: Array<{ value: string; label: string }>;
  };
  pageSize?: number;                                  // default 25
  detailPath?: (row: Record<string, unknown>) => string;  // override row-click target
  fkOptions?: Record<string, Array<{ value: string; label: string }>>;
  disableCreate?: boolean;                            // hide "+ New" button (read-only views like audit_logs)
  createCaption?: string;                             // optional hint shown below header when disableCreate (e.g., "Use the Invite User flow")
  // Bespoke create dialog override: when supplied, the "+ New" button opens this
  // instead of the generic CreateEntityModal (e.g. jurisdictions, whose create
  // flow requires a state-filtered assessor + collector — §2.2).
  renderCreateModal?: (args: { open: boolean; onClose: () => void }) => ReactNode;
}

export function GenericListByConfig(config: GenericListConfig) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  // Deep-link filters, read once at mount (the toolbar owns them thereafter) so a
  // dashboard KPI card that links here lands on exactly the set it counted — e.g.
  // /invoice-items?status=pending behind the "Billable items" card. Without this the
  // filter state started empty and the link silently opened the UNFILTERED list.
  const [filterValue, setFilterValue] = useState(
    () => (config.filterField ? searchParams.get(config.filterField.column) ?? '' : '')
  );
  // ?assigned=me narrows to the caller's own book (crud.js applies the shared
  // assignment predicate). Not a toolbar control — it only ever arrives by deep link.
  const [assignedFilter] = useState(() => searchParams.get('assigned') ?? '');

  // ── Generic deep-link filters ────────────────────────────────────────────
  // Any OTHER query param is forwarded to the list endpoint as a scalar filter.
  // Before this, only the ONE column named by `config.filterField` was honoured
  // (and only `invoice_items` configures one), so a link like
  // /referrals?capability_id=cap-duns opened the FULL list — the affordance
  // looked live and did nothing, which is worse than not linking at all.
  //
  // Safe by construction: crud.js only applies a filter whose column really
  // exists on the table (hasColumn-guarded) and enforces org + row scope
  // regardless, so an unknown param is a no-op rather than an injection point or
  // an unscoped read. The reserved names below are the list's own controls.
  const RESERVED = ['q', 'sort', 'dir', 'page', 'limit', 'assigned', 'tab'];
  const [deepFilters] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (RESERVED.includes(key)) return;
      if (config.filterField && key === config.filterField.column) return;  // owned by the toolbar
      if (value) out[key] = value;
    });
    return out;
  });
  const deepFilterEntries = Object.entries(deepFilters);
  // Gate "+ New" on the resource's can_add (mirrors the server) so a view-only role
  // (training) / agent doesn't see a create button whose write is rejected. Unmapped
  // tables → ungated (backward compatible). Hook — always runs, before any return.
  const listEntity = tableToEntity(config.table);
  const canAddEntity = useEntityPermission(listEntity ?? '__ungated__', 'can_add');
  const canCreate = !listEntity || canAddEntity;

  const apiPath = (config.apiPath ?? config.table).replace(/_/g, '-');
  const routePath = (config.routePath ?? config.table).replace(/_/g, '-');

  const schemaQuery = useQuery<{ fields: SchemaField[]; searchColumns?: string[] }>({
    queryKey: ['schema', config.table],
    queryFn: ({ signal }) => api.get(`/api/schema/${config.table}`, signal),
    staleTime: 5 * 60_000,
  });
  // A search box is offered only when the resource's list endpoint accepts `q`
  // — the schema endpoint reports the SEARCH_COLUMNS the backend LIKE-matches.
  const searchable = (schemaQuery.data?.searchColumns?.length ?? 0) > 0;

  // Server-side list: the pager + sortable headers (and the search box when the
  // table is searchable) drive GET /api/<resource>?q&sort&dir&page&limit. Keying
  // on `<apiPath>-list` keeps CreateEntityModal's invalidate working (it matches
  // by prefix, so the appended live-params object doesn't break the round-trip).
  const list = useServerList<Record<string, unknown>>({
    resource: apiPath,
    queryKey: [`${apiPath}-list`],
    defaultPageSize: config.pageSize ?? 25,
    filters: {
      ...(config.filterField ? { [config.filterField.column]: filterValue } : {}),
      ...(assignedFilter ? { assigned: assignedFilter } : {}),
      ...deepFilters,
    },
  });
  const dataQuery = list.query;

  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    const fields = schemaQuery.data?.fields ?? [];
    const visible = fields
      .filter(f => !f.hidden && !f.pk)
      .filter(f => !['organization_id', 'created_at', 'updated_at', 'created_by_user_id'].includes(f.name))
      .filter(f => !f.name.endsWith('_id') || ['client_id', 'property_id', 'parcel_id'].includes(f.name))
      .filter(f => !(config.excludeColumns ?? []).includes(f.name))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const chosen = config.columns
      ? visible.filter(f => config.columns!.includes(f.name))
      : visible.slice(0, 6);

    return chosen.map(field => ({
      accessorKey: field.name,
      header: field.label,
      cell: ({ getValue }) => formatCell(field, getValue()),
    }));
  }, [schemaQuery.data, config.columns, config.excludeColumns]);

  const { rows, total } = list;

  return (
    <>
      <PageHeader
        title={config.title}
        subtitle={
          dataQuery.isLoading
            ? 'Loading…'
            : deepFilterEntries.length
              // Say WHY the list is short. A deep link that silently narrows the
              // page looks like missing data, and the reader has no way back to
              // the full set.
              ? `${total.toLocaleString()} record${total === 1 ? '' : 's'} · filtered by ${deepFilterEntries.map(([k]) => k.replace(/_id$/, '').replace(/_/g, ' ')).join(', ')}`
              : `${total.toLocaleString()} record${total === 1 ? '' : 's'}`
        }
        actions={
          deepFilterEntries.length ? (
            <span style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
              <Button variant="secondary" size="md" onClick={() => navigate(`/${routePath}`)}>
                Show all
              </Button>
              {!config.disableCreate && canCreate ? (
                <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
                  + New {singular(config.title)}
                </Button>
              ) : null}
            </span>
          ) : !config.disableCreate && canCreate ? (
            <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
              + New {singular(config.title)}
            </Button>
          ) : config.createCaption ? (
            <span style={{
              color: 'var(--color-ink-3)',
              fontSize: 'var(--fs-sm)',
              fontStyle: 'italic',
            }}>{config.createCaption}</span>
          ) : undefined
        }
      />

      {!config.disableCreate && (
        config.renderCreateModal
          ? config.renderCreateModal({ open: createOpen, onClose: () => setCreateOpen(false) })
          : (
            <CreateEntityModal
              open={createOpen}
              onClose={() => setCreateOpen(false)}
              table={config.table}
              routePath={apiPath}
              fkOptions={config.fkOptions}
              invalidateKeys={[[`${apiPath}-list`]]}
            />
          )
      )}

      {(searchable || config.filterField) && (
        <ListToolbar
          searchValue={list.searchInput}
          onSearchChange={list.setSearchInput}
          searchPlaceholder={`Search ${config.title.toLowerCase()}…`}
          filters={config.filterField ? [{
            key: config.filterField.column,
            value: filterValue,
            onChange: setFilterValue,
            placeholder: config.filterField.placeholder,
            options: config.filterField.options,
          }] : undefined}
        />
      )}

      {dataQuery.error && (
        <ErrorBanner message={(dataQuery.error as Error).message} title={config.title} />
      )}
      {!dataQuery.isLoading && total === 0 && !dataQuery.error && (
        (list.search !== '' || filterValue !== '')
          ? <NoMatches entityName={config.title.toLowerCase()} />
          : <EmptyState entityName={config.title.toLowerCase()} />
      )}
      {(total > 0 || dataQuery.isFetching) && columns.length > 0 && !dataQuery.error && (
        <>
          <DataTable
            data={rows}
            columns={columns}
            sorting={list.sorting}
            onSortingChange={list.setSorting}
            onRowClick={(row) => {
              const dest = config.detailPath
                ? config.detailPath(row)
                : `/${routePath}/${row.id}`;
              navigate(dest);
            }}
            emptyMessage={dataQuery.isFetching ? 'Loading…' : 'No records'}
          />
          <Pagination
            pageIndex={list.pageIndex}
            pageCount={list.pageCount}
            pageSize={list.pageSize}
            totalRows={total}
            onPageChange={list.setPageIndex}
            onPageSizeChange={list.setPageSize}
          />
        </>
      )}
    </>
  );
}

/**
 * Wrapper that resolves the config from the URL ":table" param. Used
 * for routes like /v2/_entity/:table that need a single dynamic route.
 */
export function GenericListFromRoute() {
  const { table } = useParams<{ table: string }>();
  if (!table) return <p>No table specified</p>;
  return (
    <GenericListByConfig
      table={table.replace(/-/g, '_')}
      routePath={table}
      title={humanize(table)}
    />
  );
}

function formatCell(field: SchemaField, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—';
  if (field.dataType === 'boolean') {
    return value ? <Badge variant="success" dot>Yes</Badge> : <span style={{ color: 'var(--color-ink-3)' }}>No</span>;
  }
  if (field.enumOptions && Array.isArray(field.enumOptions)) {
    const opt = field.enumOptions.find(o => o.value === value);
    // Humanize so an enum value the backend didn't give a label override (e.g.
    // properties.status "in_setup", parcel_values.status "under_review") never
    // renders with a raw underscore. Idempotent on already-humanized labels.
    return <Badge variant="neutral">{humanizeToken(opt?.label ?? String(value))}</Badge>;
  }
  if (field.dataType === 'number' || field.dataType === 'integer') {
    // Year fields render bare (2026), never with a thousands separator (2,026) — §5.2.
    if (isYearFieldName(field.name)) return String(value);
    return typeof value === 'number' ? value.toLocaleString() : String(value);
  }
  // Dates & timestamps → org-timezone formatter (never a raw ISO). Detect by
  // declared type, column name, or ISO-string shape (date-consistency).
  if (
    field.dataType === 'timestamp' ||
    field.dataType === 'date' ||
    isDateFieldName(field.name) ||
    isIsoDateString(value)
  ) {
    return formatDate(value);
  }
  if (typeof value === 'string' && value.length > 60) {
    return value.slice(0, 60) + '…';
  }
  return String(value);
}

function humanize(slug: string): string {
  return slug
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function singular(title: string): string {
  return title.endsWith('s') ? title.slice(0, -1).toLowerCase() : title.toLowerCase();
}

function ErrorBanner({ message, title }: { message: string; title: string }) {
  return (
    <div style={{
      padding: 'var(--sp-4)',
      background: 'var(--color-status-red-bg)',
      border: '1px solid var(--color-status-red)',
      color: 'var(--color-status-red)',
      borderRadius: 'var(--r-lg)',
      marginBottom: 'var(--sp-4)',
    }}>
      <strong>Failed to load {title.toLowerCase()}:</strong> {message}
    </div>
  );
}

function NoMatches({ entityName }: { entityName: string }) {
  return (
    <div style={{
      padding: 'var(--sp-7)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
    }}>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>No {entityName} match your search.</p>
    </div>
  );
}

function EmptyState({ entityName }: { entityName: string }) {
  return (
    <div style={{
      padding: 'var(--sp-8)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
    }}>
      <h2 style={{
        margin: '0 0 var(--sp-2)',
        fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink-3)',
      }}>No {entityName} yet</h2>
      <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
        Click "+ New" to create one.
      </p>
    </div>
  );
}
